import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from "react";
import { CartDto, CartItemDto } from "../types";
import { AppService } from "../services/appService";
import { useAuth } from "./AuthContext";
import { message } from "antd";

interface CartContextType {
  cart: CartDto | null;
  loading: boolean;
  fetchCart: () => Promise<void>;
  removeCartItemsLocally: (itemsToRemove: CartItemDto[]) => void;
  removeCartItem: (cartItemDto: CartItemDto) => Promise<boolean>;
  addToCartLocally: (item: CartItemDto) => void;
  addToCart: (listing: any, type: string) => Promise<boolean>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const { currentUser } = useAuth();
  const [cart, setCart] = useState<CartDto | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const fetchAttemptedForUser = useRef<string | null>(null);

  const fetchCart = useCallback(async () => {
    if (!currentUser) return;
    if (fetchAttemptedForUser.current === currentUser.userName) return;

    try {
      fetchAttemptedForUser.current = currentUser.userName;
      const response = await AppService.getCart(currentUser);
      setCart(response);
    } catch (error) {
      console.error("Error fetching cart:", error);
    }
  }, [currentUser]);

  const removeCartItem = async (cartItemDto: CartItemDto): Promise<boolean> => {
    try {
      const success = await AppService.removeCartItem(cartItemDto);
      if (success) {
        setCart((prevCart) => {
          if (!prevCart || !prevCart.cartItems) return prevCart;
          const updatedItems = prevCart.cartItems.filter((item) => item !== cartItemDto);
          const newTotal = updatedItems.reduce((acc, item) => acc + (item.grossValue || 0), 0);
          return { ...prevCart, cartItems: updatedItems, grossValue: newTotal };
        });
        return true;
      }
      return false;
    } catch (error) {
      console.error("Failed to remove item from cart:", error);
      return false;
    }
  };

  const removeCartItemsLocally = useCallback((itemsToRemove: CartItemDto[]) => {
    setCart((prevCart) => {
      if (!prevCart || !prevCart.cartItems) return prevCart;
      const updatedItems = prevCart.cartItems.filter(
        (cartItem) => !itemsToRemove.some((toRemove) => toRemove.cartItemId === cartItem.cartItemId)
      );

      const newTotal = updatedItems.reduce((acc, item) => acc + (item.grossValue || 0), 0);

      return {
        ...prevCart,
        cartItems: updatedItems,
        grossValue: newTotal
      };
    });
  }, []);

  const addToCart = async (listing: any, type: string): Promise<boolean> => {
    if (type === "AIRCRAFT_PART") {
      const partId = listing.partId;

      if (partId) {
        const alreadyInCart = cart?.cartItems?.some(
          (item) => item.type === "AIRCRAFT_PART" && item.productId === partId
        );

        if (alreadyInCart) {
          message.error("Această piesă de schimb se află deja în coșul tău!");
          return false;
        }
      }
    }

    try {
      const cartItem = await AppService.addToCart(listing, type);
      if (cartItem) {
        let thumbnail = "";

        if (listing.mediaUrls && listing.mediaUrls.length > 0) {
          thumbnail = listing.mediaUrls.find((url: string) => url && url.trim() !== "") || "";
        }

        if (!thumbnail && listing.media && listing.media.length > 0) {
          const rawData = listing.media[0].mediaData;
          if (rawData) {
            if (rawData.startsWith('data:')) {
              thumbnail = rawData;
            } else {
              const prefix = rawData.startsWith('iVBOR') ? 'data:image/png;base64,' : 'data:image/jpeg;base64,';
              thumbnail = prefix + rawData;
            }
          }
        }

        if (thumbnail && (!cartItem.mediaFiles || cartItem.mediaFiles.length === 0)) {
          cartItem.mediaFiles = [thumbnail];
        }

        addToCartLocally(cartItem);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error in addToCart context:", error);
      return false;
    }
  };

  const addToCartLocally = useCallback((item: CartItemDto) => {
    setCart((prevCart) => {
      if (!prevCart) return { cartItems: [item] } as CartDto;
      return { ...prevCart, cartItems: [...(prevCart.cartItems || []), item] };
    });
  }, []);

  useEffect(() => {
    if (currentUser) {
      fetchCart();
    } else {
      fetchAttemptedForUser.current = null;
      setCart(null);
    }
  }, [currentUser, fetchCart]);

  return (
    <CartContext.Provider value={{ cart, loading: false, fetchCart, removeCartItem, removeCartItemsLocally, addToCartLocally, addToCart }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};